import { CustomError, HTTPCustomError } from './error';

import { sleep } from '../utils/sleep';

import { isPast } from 'date-fns/isPast';

type AccessToken = {
  token: string;
  expiresAt: Date;
};

type AccessTokenCache = Record<string, AccessToken>; // documentID -> AccessToken

interface GetFileToCloudSignArg {
  documentID: string;
  fileID: string;
  ankenID?: string;
}
interface PostFileToCloudSignArg {
  documentID: string;
  fileName: string;
  fileObjectURL: string;
  ankenID?: string;
}

// 端折っている
export type CloudSignDocumentModel = {
  id: string;
  files: {
    id: string;
    order: number;
  }[];
};

// Release経由でトークンを取得するところだけ抽象に
export interface RequestCloudSignTokenClient {
  requestToken: (documentID: string, ankenID?: string) => Promise<AccessToken>;
}

export class CloudSignClient {
  private static client: CloudSignClient | null;
  private requestTokenClient: RequestCloudSignTokenClient;
  private accessTokenCache: AccessTokenCache;
  private baseUrl: string;

  private constructor(client: RequestCloudSignTokenClient, baseUrl: string) {
    this.requestTokenClient = client;
    this.accessTokenCache = {};
    this.baseUrl = baseUrl;
  }

  public static init(client: RequestCloudSignTokenClient, baseUrl: string) {
    this.client = new CloudSignClient(client, baseUrl);
  }

  public static get instance() {
    if (!this.client) {
      throw new Error('CloudSignClient is not initialized');
    }
    return this.client;
  }

  private getToken(documentID: string): string | null {
    const accessToken = this.accessTokenCache[documentID];
    if (!accessToken || isPast(accessToken.expiresAt)) {
      return null;
    }
    return accessToken.token;
  }

  setToken(documentID: string, accessToken: AccessToken) {
    this.accessTokenCache[documentID] = accessToken;
  }

  private async refreshToken(documentID: string, ankenID?: string, force: boolean = false): Promise<string> {
    if (!force) {
      const token = this.getToken(documentID);
      if (token) {
        return token;
      }
    }
    const token = await this.requestTokenClient.requestToken(documentID, ankenID);
    this.setToken(documentID, token);
    return token.token;
  }

  private async decodeCustomErrorFromMsg(req: RequestInit, res: Response) {
    const resBody = (await res.json()) as Record<string, unknown>;
    const msg = resBody.message as string;
    if (msg === 'uploaded file is already signed by CloudSign') {
      return new HTTPCustomError(req, res, {
        customCode: 'cloud-sign-already-signed-document',
      });
    } else if (msg === 'failed to sign upload file') {
      return new HTTPCustomError(req, res, {
        customCode: 'cloud-sign-upload-failure',
      });
    }
    return new HTTPCustomError(req, res);
  }

  private async cloudSignFetch(
    url: string,
    documentID: string,
    ankenID?: string,
    opt?: RequestInit
  ): Promise<Response> {
    let abort = false;
    for (let i = 0; i < 3; i++) {
      try {
        const token = await this.refreshToken(documentID, ankenID);
        const options = opt || {};
        const headers = options.headers || {};

        const res = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          switch (res.status) {
            case 401: {
              // トークンが失効していたら、強制リフレッシュ
              await this.refreshToken(documentID, ankenID, true);
              continue;
            }
            case 413: {
              abort = true;
              throw new CustomError('file-size-exceeds-limit');
            }
            default: {
              if (opt) {
                const newError = await this.decodeCustomErrorFromMsg(opt, res);
                if (newError.isKnownErrorCode()) {
                  abort = true;
                }
                throw newError;
              }
              throw new CustomError(null, {
                customMessage: 'cloud sign fetch failed',
              });
            }
          }
        }
        return res;
      } catch (e) {
        if (abort) {
          throw e;
        }
      }
      await sleep(300);
    }
    throw new Error('cloud sign fetch failed');
  }

  // クラウドサインのDocumentにFileを追加
  // https://app.swaggerhub.com/apis/CloudSign/cloudsign-web_api/0.24.3#/default/post_documents__documentID__files
  async postFile({
    documentID,
    fileObjectURL,
    fileName,
    ankenID,
  }: PostFileToCloudSignArg): Promise<CloudSignDocumentModel> {
    const blob = await fetch(fileObjectURL).then((res) => res.blob());

    const formData = new FormData();
    formData.append('name', fileName);
    formData.append('uploadfile', new File([blob], fileName));

    const model = await this.cloudSignFetch(`${this.baseUrl}/documents/${documentID}/files`, documentID, ankenID, {
      method: 'POST',
      body: formData,
    }).then((res) => res.json() as Promise<CloudSignDocumentModel>);
    return model;
  }

  // クラウドサインのFileを取得
  // https://app.swaggerhub.com/apis/CloudSign/cloudsign-web_api/0.24.3#/default/get_documents__documentID__files__fileID_
  async getFile({ documentID, fileID, ankenID }: GetFileToCloudSignArg): Promise<Response> {
    return this.cloudSignFetch(`${this.baseUrl}/documents/${documentID}/files/${fileID}`, documentID, ankenID, {
      method: 'GET',
    });
  }

  // クラウドサインのFileを取得(URLを直接指定バージョン)
  async getFileFromURL(url: string, documentID: string, ankenID?: string): Promise<Response> {
    return this.cloudSignFetch(url, documentID, ankenID, {
      method: 'GET',
    });
  }
}
